/**
 * SingleRegionSelect — 基于 NewRegionSelect 的单选版本
 * 去掉所有 Checkbox，左侧 Region 列表点击展开右侧国家，右侧国家点击即选中并关闭面板
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Dropdown, Icon, Input, Bubble } from 'tea-component';
import { LOCALE_CN } from '@/pages/home/components/NewRegionSelect/config';
import { getText, getBubbleContentText } from '@/pages/home/components/NewRegionSelect/util';
import useLocale from '@/pages/home/components/NewRegionSelect/useLocale';
import classNames from 'classnames';
import { isEmpty, trim } from 'lodash-es';
import { List as VirtualizedList } from 'react-virtualized';
import '@/pages/home/components/NewRegionSelect/index.scss';
import './index.scss';

export interface ISingleRegionSelectOption {
  value: string;
  text: string;
  textCN?: string;
  icon?: string;
  disabled?: boolean;
  bubbleContent?: string | React.ReactElement;
  bubbleContentCN?: string | React.ReactElement;
  children?: ISingleRegionSelectOption[];
}

export interface ISingleRegionSelectProps {
  /** 当前选中值 (country value) */
  value?: string;
  /** Region → Country 层级数据 */
  options: ISingleRegionSelectOption[];
  /** 选中回调 */
  onChange?: (value: string) => void;
  /** 面板关闭回调 */
  onClose?: (value: string | undefined) => void;
  locale?: string;
  language?: any;
  placeholder?: string;
  /** 是否非国家地区图标 */
  isNotRegionIcon?: boolean;
  loading?: boolean;
  error?: boolean;
  className?: string;
  boxClassName?: string;
  rightListWidth?: number;
  rightListHeight?: number;
}

const SingleRegionSelect: React.FC<ISingleRegionSelectProps> = (props) => {
  const {
    value,
    options = [],
    onChange,
    onClose,
    locale = LOCALE_CN,
    language,
    placeholder,
    isNotRegionIcon = false,
    loading = false,
    boxClassName = '',
    rightListWidth,
    rightListHeight,
    ...restProps
  } = props;

  const { formatMessage } = useLocale(locale, language);
  const [search, setSearch] = useState('');
  const [selectedRegion, setSelectedRegion] = useState<string>('');
  const searchInputRef = useRef<any>(null);
  const dropdownRef = useRef<any>(null);
  const currentValueRef = useRef<string | undefined>(value);

  // Sync external value
  useEffect(() => {
    currentValueRef.current = value;
  }, [value]);

  // Auto-select first region on options load
  useEffect(() => {
    if (!loading && options.length > 0 && !selectedRegion) {
      setSelectedRegion(options[0]?.value || '');
    }
  }, [loading, options]);

  // All children flattened
  const allChildren = useMemo(() => {
    const result: Array<ISingleRegionSelectOption & { parent: string; parentText?: string; parentTextCN?: string }> = [];
    options.forEach((region) => {
      (region.children || []).forEach((child) => {
        result.push({
          ...child,
          parent: region.value,
          parentText: region.text,
          parentTextCN: region.textCN,
        });
      });
    });
    return result;
  }, [options]);

  // Filtered options based on search
  const { filteredOptions, filteredChildren } = useMemo(() => {
    const searchStr = trim(search).toLowerCase();
    if (!searchStr) {
      return { filteredOptions: options, filteredChildren: allChildren };
    }

    const fChildren: typeof allChildren = [];
    const fOptions = options
      .map((region) => {
        const regionMatch =
          region.text?.toLowerCase().includes(searchStr) ||
          region.textCN?.toLowerCase().includes(searchStr);

        if (regionMatch) {
          (region.children || []).forEach((child) => {
            fChildren.push({ ...child, parent: region.value, parentText: region.text, parentTextCN: region.textCN });
          });
          return region;
        }

        const matchedChildren = (region.children || []).filter(
          (child) =>
            child.text?.toLowerCase().includes(searchStr) ||
            child.textCN?.toLowerCase().includes(searchStr),
        );

        if (matchedChildren.length > 0) {
          matchedChildren.forEach((child) => {
            fChildren.push({ ...child, parent: region.value, parentText: region.text, parentTextCN: region.textCN });
          });
          return { ...region, children: matchedChildren };
        }

        return null;
      })
      .filter(Boolean) as ISingleRegionSelectOption[];

    return { filteredOptions: fOptions, filteredChildren: fChildren };
  }, [options, allChildren, search]);

  // Auto-select first region when search changes
  useEffect(() => {
    if (trim(search) && filteredOptions.length > 0) {
      setSelectedRegion(filteredOptions[0]?.value || '');
    }
  }, [search]);

  // Right-side market list
  const marketsList = useMemo(() => {
    return filteredChildren.filter((item) => item.parent === selectedRegion);
  }, [filteredChildren, selectedRegion]);

  // Selected region display name
  const selectedRegionName = useMemo(() => {
    const regionItem = options.find((item) => item.value === selectedRegion);
    return {
      'en-US': regionItem?.text,
      'zh-CN': regionItem?.textCN,
    };
  }, [selectedRegion, options]);

  // Highlight search text
  const highlightText = (text: string, searchStr: string) => {
    if (!searchStr) return text;
    const escaped = searchStr.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&');
    const regex = new RegExp(`(${escaped})`, 'gi');
    const parts = text.split(regex);
    return (
      <>
        {parts.map((part, index) =>
          regex.test(part) ? (
            <span key={index} style={{ color: '#326ff0' }}>{part}</span>
          ) : (
            part
          ),
        )}
      </>
    );
  };

  // Handle country click — single select
  const handleSelectCountry = useCallback(
    (countryValue: string) => {
      currentValueRef.current = countryValue;
      onChange?.(countryValue);
      // Close dropdown after selection
      dropdownRef.current?.close?.();
    },
    [onChange],
  );

  const handleClose = useCallback(() => {
    onClose?.(currentValueRef.current);
    setSearch('');
  }, [onClose]);

  // Focus search on open
  useEffect(() => {
    searchInputRef.current?.focus();
  });

  // Button render
  const buttonContent = useMemo(() => {
    const titleText = placeholder || formatMessage({ id: 'button_market' });
    if (!value) {
      return <span className="dbo-single-region-select-btn__placeholder">{titleText}</span>;
    }
    const found = allChildren.find((c) => c.value === value);
    if (found) {
      return (
        <>
          <span className="dbo-single-region-select-btn__label">{titleText}</span>
          {found.icon && <img className="dbc-region-select-item-icon" src={found.icon} alt="" />}
          <span className="dbo-single-region-select-btn__value">{getText(found, locale)}</span>
        </>
      );
    }
    return (
      <>
        <span className="dbo-single-region-select-btn__label">{titleText}</span>
        <span className="dbo-single-region-select-btn__value">{value}</span>
      </>
    );
  }, [value, allChildren, placeholder, formatMessage, locale]);

  const imgInvert = ['Steam'];

  return (
    <Dropdown
      button={
        <div className="dbo-single-region-select-btn">
          {buttonContent}
        </div>
      }
      size="m"
      className="dbo-region-select dbo-single-region-select"
      appearance="button"
      clickClose={false}
      closeOnScroll
      onClose={handleClose}
      boxClassName={`dbc-region-select-container ${boxClassName}`}
      overlayClassName="dbc-region-select-box"
      ref={dropdownRef}
      {...restProps}
    >
      {() => (
        <>
          {/* Search */}
          <div className="dbc-region-select-search-container">
            <div className="dbc-region-select-search-wrap">
              <div className="dbc-region-select-search-input-wrap">
                <div className="dbc-region-select-search-input-wrap-inner">
                  <Input
                    placeholder={formatMessage({ id: 'search' })}
                    value={search}
                    ref={searchInputRef}
                    className="dbc-region-select-search-input"
                    type="text"
                    onChange={(val) => setSearch(val)}
                  />
                </div>
                <div className="dbc-region-select-search-icon">
                  {search ? (
                    <Icon type="dismiss" onClick={() => setSearch('')} />
                  ) : null}
                  <Icon type="search" />
                </div>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="dbc-region-select-content-container">
            {/* Left: Regions */}
            <div className="dbc-region-select-content-wrap">
              <div className="dbc-region-select-content-title-wrap">
                <span>{formatMessage({ id: 'region_title' })}</span>
              </div>
              {filteredOptions.length > 0 ? (
                <div className="dbc-region-select-item-wrap">
                  {filteredOptions.map((region) => {
                    const title = highlightText(getText(region, locale) || '', search);
                    const isActive = region.value === selectedRegion;
                    return (
                      <div
                        key={region.value}
                        className={classNames('dbo-single-region-select__region-item', {
                          active: isActive,
                        })}
                        onClick={() => setSelectedRegion(region.value)}
                      >
                        {region.icon && (
                          <img
                            className={`dbc-region-select-item-icon ${isNotRegionIcon ? 'device-icon' : ''}`}
                            src={region.icon}
                            alt=""
                          />
                        )}
                        <div className="dbc-region-select-checkbox-name-wrap">
                          <span className="dbc-region-select-checkbox-name">
                            {title}&nbsp;({(region.children || []).length})
                          </span>
                          <Icon type="arrowright" />
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="dbc-region-select-item-none">
                  {formatMessage({ id: 'country_no_data' })}
                </div>
              )}
            </div>

            {/* Right: Countries */}
            <div className="dbc-region-select-content-wrap">
              <div className="dbc-region-select-content-title-wrap">
                <span className="dbc-region-select-content-country-title">
                  {selectedRegionName?.[locale]}
                </span>
              </div>
              {marketsList.length > 0 ? (
                <div className="dbc-region-select-item-wrap">
                  <VirtualizedList
                    rowCount={marketsList.length}
                    rowRenderer={({ index, style }: { index: number; style: React.CSSProperties }) => {
                      const item = marketsList[index];
                      const title = highlightText(getText(item, locale) || '', search);
                      const isSelected = item.value === value;
                      return (
                        <div style={style} key={index}>
                          <Bubble
                            dark
                            arrowPointAtCenter
                            closeOnScroll
                            content={getBubbleContentText(item as any, locale)}
                          >
                            <div
                              className={classNames('dbo-single-region-select__market-item', {
                                'dbo-single-region-select__market-item--selected': isSelected,
                                'dbo-single-region-select__market-item--disabled': item.disabled,
                              })}
                              onClick={() => {
                                if (item.disabled) return;
                                handleSelectCountry(item.value);
                              }}
                            >
                              {item.icon && (
                                <img
                                  className={`dbc-region-select-item-icon ${isNotRegionIcon ? 'device-icon' : ''} ${imgInvert.includes(getText(item, locale) || '') ? 'db-invert-unset' : ''}`}
                                  src={item.icon}
                                  alt=""
                                />
                              )}
                              <span>{title}</span>
                              {isSelected && <Icon type="success" />}
                            </div>
                          </Bubble>
                        </div>
                      );
                    }}
                    width={rightListWidth ?? 258}
                    height={rightListHeight ?? 305}
                    rowHeight={36}
                    overscanRowCount={5}
                    scrollToIndex={0}
                  />
                </div>
              ) : (
                <div className="dbc-region-select-item-none">
                  {formatMessage({ id: 'country_no_data' })}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </Dropdown>
  );
};

export default SingleRegionSelect;
